import { memo, useEffect, useState } from "react";

import {
  useMobileVerificationContext,
  usePersonalDetailsContext,
  useZenithAccountUpgradeContext,
} from "@zenith/context";
import { AppNavigation, AppStepper } from "@zenith/hooks";
import {
  SpinnerComponent,
  TermsAndConditionsComponent,
  TextFieldComponent,
} from "@zenith/components";
import { NavLink, useNavigate } from "react-router-dom";
import {
  BVN_LINKED_WITH_ANOTHER_USER_BODY,
  USER_ACCOUNT_NOT_LINKED_WITH_BVN_BODY,
  ACCOUNT_NUMBER_LENGTH,
  INACTIVE_ACCOUNT_MODAL_BODY,
  INACTIVE_ACCOUNT_MODAL_TITLE,
  MULTIPLE_SIGNATURE_ACCOUNT_BODY,
  PHONE_NUMBER_IS_INVALID,
  TERMS_AND_CONDITIONS,
  ZENITH_ACCOUNT_MODAL_BUTTON,
  USER_ACCOUNT_NOT_FOUND_BODY,
  CLIENT_INTEGRATION_ERROR_BODY,
  SUCCESS_API_CODE,
  SOMETHING_WENT_WRONG_TITLE,
  INCORRECT_ACCOUNT_NUMBER_BODY,
  UNABLE_TO_GET_CUSTOMER_DETAILS_BODY,
  BVN_LINKED_DOB_ISSUE,
} from "@zenith/data/constants";
import {
  ApiResponseDTOUsersOnboardingResponseDTO,
  ApiResponseDTOZenzAccountVerifyResponseDTO,
  OnboardingApiService,
  TierApiService,
  UpgradeTierZenithAccountRequestDTO,
  UsersApiService,
  UsersOnboardingRequestDTO,
  UsersOnboardingResponseDTO,
  ZenzAccountVerifyRequestDTO,
} from "@zenith/services/user";
import { setAccountNumberValidations } from "@zenith/features/Authentication/validations/AuthValidations";
import ModalComponent from "@zenith/components/ui/ModalComponent/modal-component/modal-component";
import {
  SIGN_UP,
  UPGRADE_ACCOUNT,
  ACCOUNT_CREATED,
} from "@zenith/routes/paths";
import { Button } from "@mui/material";
import { countryCodeToEnum } from "@zenith/utils";

/* zenith account */
/* eslint-disable-next-line */
export interface SignUpZenithAccountPageProps {}
interface ExtendedUsersOnboardingResponseDTO
  extends UsersOnboardingResponseDTO {
  mismatchFoundInDocument?: boolean;
}

const SignUpZenithAccountPage = memo(function SignUpZenithAccountPage(
  props: SignUpZenithAccountPageProps
) {
  const { navigateTo } = AppNavigation();
  const { nextStep, maxStep, activeStep } = AppStepper();
  const navigate = useNavigate();
  const { setPersonalDetailsContext } = usePersonalDetailsContext();

  const {
    setAccountNumber,
    zenithAccountUpgradeContext: { accountNumber, isUpgrade },
  } = useZenithAccountUpgradeContext();

  const {
    mobileVerificationContext: { countryCode, phoneNumber },
    setFirstName,
  } = useMobileVerificationContext();

  useEffect(() => {
    if (!countryCode && !phoneNumber && !isUpgrade) {
      handleBackButton();
    }
  });

  const [modalText, setModalText] = useState({
    title: "",
    body: "",
  });
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hideButton, setHideButton] = useState(false);
  const [isFormEdited, setIsFormEdited] = useState(false);

  const [formData, setFormData] = useState<{
    accountNumber: string;
  }>({
    accountNumber: "",
  });

  const [formDataErrors, setFormDataErrors] = useState<{
    accountNumber: {
      hasError: boolean;
      message: string;
    };
  }>({
    accountNumber: {
      hasError: false,
      message: "",
    },
  });

  const [isChecked, setIsChecked] = useState(false);

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsChecked(event.target.checked);
  };

  const onAccountNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const onlyNumbers = new RegExp("^[0-9]*$");
    const accountNumber = event.currentTarget.value;
    if (!onlyNumbers.test(accountNumber)) return;
    setFormDataErrors({
      accountNumber: {
        message: "",
        hasError: false,
      },
    });
    setFormData({
      ...formData,
      accountNumber,
    });
    setIsFormEdited(true);
  };

  const onAccountNumberBlur = () => {
    setFormDataErrors({
      accountNumber: setAccountNumberValidations(
        formData.accountNumber,
        ACCOUNT_NUMBER_LENGTH
      ),
    });
  };

  const handleCreateAccount = () => {
    const request: UpgradeTierZenithAccountRequestDTO = {
      accountNumber: accountNumber,
    };
    if (activeStep >= maxStep - 1 && !isUpgrade) {
      OnboardingApiService.createAccount({
        phoneNumber: phoneNumber,
        countryCode: countryCodeToEnum(countryCode),
      })
        .then((response) => {
          if (
            "data" in response &&
            response.data &&
            response.internalCode === SUCCESS_API_CODE
          ) {
            setFirstName(response.data.firstName as string);
            navigateTo(ACCOUNT_CREATED, {
              state: { accountInfo: response.data },
            });
          }
        })
        .catch((error) => {
          throw Error(error);
        });
    } else if (activeStep >= maxStep - 1 && isUpgrade) {
      TierApiService.upgradeTierZenithAccount(request)
        .then((response) => {
          if ("data" in response && response.data) {
            setFirstName(response.data.firstName as string);
            navigateTo(ACCOUNT_CREATED, {
              state: { accountInfo: response.data },
            });
          }
        })
        .catch((error) => {
          navigateTo(ACCOUNT_CREATED, {
            state: { upgradeError: error.body },
          });
        });
    } else {
      nextStep();
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    setIsLoading(true);
    event.preventDefault();

    setFormDataErrors({
      accountNumber: setAccountNumberValidations(formData.accountNumber),
    });
    if (formDataErrors.accountNumber.hasError) return;

    const zenithAccountUpgradeRequest: ZenzAccountVerifyRequestDTO = {
      accountNumber: formData.accountNumber,
    };
    const request: UsersOnboardingRequestDTO = {
      accountNumber: formData.accountNumber,
      countryCode,
      phoneNumber,
    };

    if (!isUpgrade) {
      OnboardingApiService.setCustomerAccount(request)
        .then((response: ApiResponseDTOUsersOnboardingResponseDTO) => {
          const responseData =
            response.data as ExtendedUsersOnboardingResponseDTO;
          if (responseData.mismatchFoundInDocument) {
            setPersonalDetailsContext({
              documentPhoneNumber: responseData.phoneNumber || "",
              mismatchFoundInDocument:
                responseData.mismatchFoundInDocument || false,
            });
            setIsLoading(false);
            nextStep();
          } else {
            // handleCreateAccount();
            nextStep();
          }
        })
        .catch((error) => {
          setIsLoading(false);
          const internalCode = error.body.internalCode;
          if (internalCode === "ZENZ-CROSS-008") {
            setFormDataErrors({
              accountNumber: {
                message: "Zenith account number not valid",
                hasError: true,
              },
            });
            setIsLoading(false);
          } else if (internalCode === "ZENZ-CROSS-004") {
            setFormDataErrors({
              accountNumber: {
                message: PHONE_NUMBER_IS_INVALID,
                hasError: true,
              },
            });
            setIsLoading(false);
          } else if (internalCode === "ZENZ-USER-007") {
            setShowModal(true);
            setModalText({
              title: INACTIVE_ACCOUNT_MODAL_TITLE,
              body: INACTIVE_ACCOUNT_MODAL_BODY,
            });
            return;
          } else if (internalCode === "ZENZ-USER-008") {
            setShowModal(true);
            setModalText({
              title: SOMETHING_WENT_WRONG_TITLE,
              body: MULTIPLE_SIGNATURE_ACCOUNT_BODY,
            });
            setIsLoading(false);
            return;
          } else if (internalCode === "ZENZ-USER-016") {
            setShowModal(true);
            setModalText({
              title: SOMETHING_WENT_WRONG_TITLE,
              body: USER_ACCOUNT_NOT_FOUND_BODY,
            });
            setIsLoading(false);
            return;
          } else if (internalCode === "ZENZ-USER-026") {
            setShowModal(true);
            setModalText({
              title: SOMETHING_WENT_WRONG_TITLE,
              body: USER_ACCOUNT_NOT_LINKED_WITH_BVN_BODY,
            });
            setHideButton(false);
            setIsLoading(false);
            return;
          } else if (internalCode === "ZENZ-USER-027") {
            setShowModal(true);
            setModalText({
              title: SOMETHING_WENT_WRONG_TITLE,
              body: BVN_LINKED_WITH_ANOTHER_USER_BODY,
            });
            setIsLoading(false);
            return;
          } else if (internalCode === "ZENZ-USER-034") {
            setShowModal(true);
            setModalText({
              title: SOMETHING_WENT_WRONG_TITLE,
              body: BVN_LINKED_DOB_ISSUE,
            });
            setIsLoading(false);
            return;
          } else if (internalCode === "ZENZ-USER-039") {
            setShowModal(true);
            setModalText({
              title: SOMETHING_WENT_WRONG_TITLE,
              body: INCORRECT_ACCOUNT_NUMBER_BODY,
            });
            setHideButton(false);
            setIsLoading(false);
            return;
          } else if (internalCode === "ZENZ-USER-040") {
            setShowModal(true);
            setModalText({
              title: SOMETHING_WENT_WRONG_TITLE,
              body: UNABLE_TO_GET_CUSTOMER_DETAILS_BODY,
            });
            setIsLoading(false);
            return;
          } else if (internalCode === "ZENZ-CROSS-012") {
            setShowModal(true);
            setModalText({
              title: SOMETHING_WENT_WRONG_TITLE,
              body: CLIENT_INTEGRATION_ERROR_BODY,
            });
            setHideButton(true);
            setIsLoading(false);
          }
          setIsFormEdited(false);
        });
    } else {
      setAccountNumber(formData.accountNumber as string);
      UsersApiService.verifyZenzAccountNumber(zenithAccountUpgradeRequest)
        .then((response: ApiResponseDTOZenzAccountVerifyResponseDTO) => {
          if ( activeStep >= maxStep - 1) {
            const upgradeRequest: UpgradeTierZenithAccountRequestDTO = {
            accountNumber: formData.accountNumber,
            };
          TierApiService.upgradeTierZenithAccount(upgradeRequest)
            .then((upgradeResponse) => {
              if ("data" in upgradeResponse && upgradeResponse.data) {
                setFirstName(upgradeResponse.data.firstName as string);
                navigateTo(ACCOUNT_CREATED, {
                  state: { accountInfo: upgradeResponse.data },
                });
              }
            })
            .catch((upgradeError) => {
              navigateTo(ACCOUNT_CREATED, {
                state: { upgradeError: upgradeError.body },
              });
            });
          } else {
              setIsLoading(false);
              nextStep();
            }
          })
        .catch((error) => {
          const internalCode = error.body.internalCode;
          if (internalCode === "ZENZ-CROSS-008") {
            setFormDataErrors({
              accountNumber: {
                message: "Zenith account number not valid",
                hasError: true,
              },
            });
            setIsLoading(false);
          }
        });
      setIsFormEdited(false);
    }
  };

  const handleBackButton = () => {
    if (!isUpgrade) {
      navigateTo(SIGN_UP);
    } else {
      navigateTo(UPGRADE_ACCOUNT);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <ModalComponent
        title={modalText.title}
        body={modalText.body}
        showModal={showModal}
        onClickClose={() => setShowModal(false)}
        onClickButton={() => navigateTo(SIGN_UP)}
        buttonText={ZENITH_ACCOUNT_MODAL_BUTTON}
        widerButton={true}
        hideButtons={hideButton}
      />
      <div className="overflow-hidden">
        <h5 className="text-4xl font-bold">
          Connect With Your <br /> Zenith Account
        </h5>
        <div className="min-w-0 text-base">
          Kindly provide your Zenith Bank account details
        </div>
      </div>

      <div className="mt-10 flex flex-col gap-y-7">
        <div>
          <TextFieldComponent
            id="zenithAccountNumberField"
            label="Zenith Account Number"
            labelId="zenithAccountNumberFieldLabel"
            type="text"
            placeholder="Enter Zenith account number"
            value={formData.accountNumber}
            maxLength={ACCOUNT_NUMBER_LENGTH}
            minLength={ACCOUNT_NUMBER_LENGTH}
            hasError={formDataErrors.accountNumber.hasError}
            onBlur={onAccountNumberBlur}
            onChange={onAccountNumberChange}
            disabled={false}
          />
          {formDataErrors.accountNumber.hasError && (
            <div className="mt-1 ml-2 text-red-400">
              <span className="break-words">
                {formDataErrors.accountNumber.message}
              </span>
            </div>
          )}
        </div>
        <div>
          <TermsAndConditionsComponent
            text="checkbox"
            to={TERMS_AND_CONDITIONS}
            onCheckboxChange={handleCheckboxChange}
            isChecked={isChecked}
          />
        </div>
      </div>

      <div className="stepper-buttons-container">
        <button
          id="back-button"
          className="secondary-button"
          onClick={handleBackButton}
          type="button"
        >
          Back
        </button>
        <Button
          id="continue-button"
          type="submit"
          disabled={
            formData.accountNumber.length < ACCOUNT_NUMBER_LENGTH ||
            !isChecked ||
            !isFormEdited
          }
          className={`primary-button h-12 w-[240px] self-center rounded-lg bg-primary-300 py-3 px-10 text-base font-medium normal-case text-white hover:bg-primary-300 ${
            formData.accountNumber.length < ACCOUNT_NUMBER_LENGTH ||
            !isChecked ||
            isLoading
              ? "cursor-not-allowed opacity-50"
              : ""
          }`}
        >
          <span>Continue </span>
          {isLoading && <SpinnerComponent size={20} type={"secondary"} />}
        </Button>
      </div>
      {!isUpgrade ? (
        <div className="mt-7">
          <span className="text-sm font-normal leading-snug text-neutral-500">
            Continue without Zenith account?{" "}
            <NavLink to={SIGN_UP} className="underline">
              Click Here
            </NavLink>
          </span>
        </div>
      ) : (
        ""
      )}
    </form>
  );
});

export default SignUpZenithAccountPage;
